import { AfterViewInit, OnDestroy, Component, Inject } from '@angular/core';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';

import { PartConfig } from 'app/collage-designer/collage-designer-config';
import { Button } from 'app/dialogs/dialog-buttons.component';
import { ProductChooserWarningDialogComponent
       } from 'app/dialogs/productchooser-warning-dialog/productchooser-warning-dialog.component'; //eslint-disable-line
import { ProductConfig } from 'app/product-designer-config';
import { ConditionMatcher } from 'collage-designer/condition-matcher';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { ConfigService } from 'core/config/config.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { AsyncEventEmitterSubscriber
       } from 'core/utils/async-event-emitter';

interface ProductGroup
{
  variant: string;
  options: ProductConfig[];
  iconURL?: string;
}

@Component({
    selector: 'product-chooser',
    templateUrl: './product-chooser.component.html',
    styleUrls: ['./product-chooser.component.scss'],
    standalone: false
})
export class ProductChooserComponent implements AfterViewInit, OnDestroy
{
  public product: ProductConfig;
  public productGroups: ProductGroup[];

  private producthasChangedSubscription:
      AsyncEventEmitterSubscriber<ProductConfig>;
  private conditionMatcher: ConditionMatcher;


  constructor(
      private dialog: MatDialog,
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(ProductService) private productService: ProductService,
      @Inject(ConfigService) private configService: ConfigService,
      @Inject(EventTrackingService) private eventTrackingService:
          EventTrackingService)
  {
    const options = this.productService.getProducts();
    this.productGroups = this.buildProductGroups(options);
    this.product = this.productService.getCurrentProduct();
  }

  public ngAfterViewInit(): void
  {
    this.producthasChangedSubscription =
        this.productService.productHasChanged.subscribe(
            async (productConfig) => {
          this.product = productConfig;
        });
  }

  public ngOnDestroy(): void
  {
    this.productService.productHasChanged.unsubscribe(
        this.producthasChangedSubscription);
  }

  public async setCurrentProduct(
      productConfig: ProductConfig, option: MatOption): Promise<void>
  {
    if (await this.getMotivePart(this.product.id) &&
        !await this.checkIfCurrentTemplateExistsInTargetPart(
            productConfig.id) &&
        !await this.confirmSwitchDespiteDesignLoss(productConfig)) {
      option.deselect();
      return;
    }

    const oldProductId = this.productService.getCurrentProduct().id;

    if (oldProductId !== productConfig.id) {
      await this.productService.setCurrentProduct(productConfig.id);

      this.eventTrackingService.track({
        category: productConfig.id,
        action: 'product_change',
        label: oldProductId,
      });
    }
  }

  private buildProductGroups(options: ProductConfig[]): ProductGroup[]
  {
    const groups = options.reduce((acc, option) => {
      if (!acc[option.variant]) {
        acc[option.variant] = [];
      }

      acc[option.variant].push(option);

      return acc;
    }, {} as Record<string, ProductConfig[]>);

    const groupedProducts = Object.entries(groups)
      .map(([variant, options]) => ({
        variant,
        options: options,
        iconURL: options.find(o => o.iconURL)?.iconURL,
      }));

    return groupedProducts;
  }

  private async confirmSwitchDespiteDesignLoss(
      targetProduct: ProductConfig): Promise<boolean>
  {
    const result = await this.dialog
        .open(ProductChooserWarningDialogComponent, {
          data: {
            productVariant: targetProduct.name ?? targetProduct.shortName,
            currentProductVariant: this.product.name ?? this.product.shortName,
          },
          panelClass: 'dialog-pane',
          disableClose: true,
        })
        .afterClosed()
        .toPromise();

    return result === Button.Choice.CONTINUE;
  }

  private async getMotivePart(targetProductId: string):
      Promise<PartConfig|undefined>
  {
    const resolvedProduct =
        await this.productService.getProductConfigById(targetProductId);
    const steps = resolvedProduct.collageDesignerConfig?.steps;
    const motivePart = steps?.[0]?.parts?.find(part => part.id === 'motive');

    return motivePart;
  }


  /**
   * @throws Error
   */
  private async checkIfCurrentTemplateExistsInTargetPart(
      targetProductId: string): Promise<boolean>
  {
    const targetMotivePart = await this.getMotivePart(targetProductId);

    if (!targetMotivePart) {
      return false;
    }

    const currentMotiveTemplate =
        this.designStepService.getTemplateIdByStepId(0);

    if (!currentMotiveTemplate) {
      return false;
    }

    const conditionMatcher = new ConditionMatcher(
        this.configService.getProductDesignerComponentConfig().catalogStoreId,
        {
          getProductId:() => targetProductId,
          getSVGStructureByPartId: () => { // won't be reached
            throw new Error(
              'not implemented for first designStep');
          }
        });

    for (const group of targetMotivePart.templateGroups) {
      if (currentMotiveTemplate in group.templates &&
          await conditionMatcher.areConditionsFullfilled(group.conditions)) {
        return true;
      }
    }
    return false;
  }
}
